// src/features/admin/supportApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ── Types ──────────────────────────────────────────────────────────────────────

export type TicketStatusNum = 0 | 1 | 2 | 3;

export type TicketChannel = 'help_resolution' | 'email' | 'whatsapp' | 'tollfree' | 'livechat';

export const STATUS_LABEL: Record<TicketStatusNum, string> = {
  0: 'Open',
  1: 'In Progress',
  2: 'Resolved',
  3: 'Closed',
};

export interface TicketProduct {
  product_id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  image_url?: string | null;
}

export interface SupportTicket {
  id:           number;
  subject:      string;
  description:  string;
  status:       TicketStatusNum;
  order_id?:    number | null;
  invoice_no?:  string | null;
  created_at?:  string;
  channel:             TicketChannel;
  nature_of_complaint: string;
  raised_by_admin:     boolean;
  is_guest:       boolean;
  user_id?:       number | null;
  guest_user_id?: string | null;
  display_name: string;
  email:        string;
  phone:        string;
  product_ids?: number[];
  products?:    TicketProduct[];
}

// Update the TicketReply interface
export interface TicketReply {
  id?: number;
  message: string;
  created_at?: string;
  sender_type?: 'admin' | 'customer';
  sender_name?: string;
}

// Update TicketConversation interface
export interface TicketConversation {
  ticket: { 
    subject: string; 
    status: TicketStatusNum;
    products?: TicketProduct[];
    product_ids?: number[];
    display_name?: string;
  };
  replies: TicketReply[];
}

export interface UserOrder {
  order_id:   number;
  invoice_no: string;
  order_date: string;
  status:     number;
  total:      number;
  order_type: 'regular' | 'guest';
}

export interface UserOrdersResponse {
  error:   boolean;
  user_id: number;
  orders:  UserOrder[];
}

export interface OrderProduct {
  product_id:   number;
  product_name: string;
  quantity:     number;
  unit_price:   number;
  image_url?:   string | null;
}

export interface OrderProductsResponse {
  error:      boolean;
  order_id:   number;
  invoice_no: string;
  order_type: 'regular' | 'guest';
  products:   OrderProduct[];
}

export interface CreateTicketPayload {
  channel:             TicketChannel;
  name:                string;
  email:               string;
  phone:               string;
  order_id:            number | string;
  product_ids:         number[];
  subject:             string;
  nature_of_complaint: string;
  description:         string;
  status:              TicketStatusNum;
}

export interface CreateTicketResponse {
  success:   boolean;
  message:   string;
  ticket_id: number;
}

// ── Base query ─────────────────────────────────────────────────────────────────

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('admin_token');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
    return headers;
  },
});

// ── API slice ──────────────────────────────────────────────────────────────────

export const supportApi = createApi({
  reducerPath: 'supportApi',
  baseQuery,
  tagTypes: ['Ticket'],
  endpoints: (build) => ({

    listAllTickets: build.query<{ success: boolean; tickets: SupportTicket[] }, void>({
      query: () => ({ url: 'admin/support-tickets', method: 'GET' }),
      providesTags: ['Ticket'],
    }),

    // All orders for a specific user (lazy — triggered when user is selected)
    getUserOrders: build.query<UserOrdersResponse, number>({
      query: (user_id) => ({
        url:    'admin/support-user-orders',
        method: 'POST',
        body:   { user_id },
      }),
    }),

    // Products inside an order (lazy — triggered when order is selected)
    getOrderProducts: build.query<OrderProductsResponse, number>({
      query: (order_id) => ({
        url:    'admin/support-order-products',
        method: 'POST',
        body:   { order_id },
      }),
    }),

    getConversation: build.query<TicketConversation, number>({
      query: (ticket_id) => ({
        url:    'admin/support-get-ticket',
        method: 'POST',
        body:   { ticket_id },
      }),
      providesTags: (_r, _e, id) => [{ type: 'Ticket', id }],
      transformResponse: (response: any) => {
        if (response.success && response.ticket) {
          return {
            ticket: {
              subject: response.ticket.subject,
              status: response.ticket.status,
              products: response.ticket.products || [],
              product_ids: response.ticket.product_ids || []
            },
            replies: response.replies || []
          };
        }
        return { ticket: { subject: '', status: 0, products: [], product_ids: [] }, replies: [] };
      },
    }),

    sendReply: build.mutation<
      { success: boolean; message: string },
      { ticket_id: number; message: string; status?: TicketStatusNum }
    >({
      query: (body) => ({ url: 'admin/support-reply', method: 'POST', body }),
      invalidatesTags: (_r, _e, arg) => ['Ticket', { type: 'Ticket', id: arg.ticket_id }],
    }),

    updateTicketStatus: build.mutation<
      { success: boolean; message: string },
      { ticket_id: number; status: TicketStatusNum }
    >({
      query: (body) => ({ url: 'admin/support-update-status', method: 'POST', body }),
      invalidatesTags: ['Ticket'],
    }),

    createTicket: build.mutation<CreateTicketResponse, CreateTicketPayload>({
      query: (body) => ({ url: 'admin/support-create-ticket', method: 'POST', body }),
      invalidatesTags: ['Ticket'],
    }),

    deleteTicket: build.mutation<{ success: boolean; message: string }, number>({
      query: (ticket_id) => ({ 
        url: 'admin/support-delete-ticket', 
        method: 'POST', 
        body: { ticket_id } 
      }),
      invalidatesTags: ['Ticket'],
    }),

  }),
});

export const {
  useListAllTicketsQuery,
  useLazyGetUserOrdersQuery,
  useLazyGetOrderProductsQuery,
  useGetConversationQuery,
  useSendReplyMutation,
  useUpdateTicketStatusMutation,
  useCreateTicketMutation,
  useDeleteTicketMutation,
} = supportApi;